/**
 * The Explained panel: every term the portal uses, in plain words, beside the page instead of on a
 * separate one (Andres, 2026-09-23: fewer clicks, one dashboard). Open by default on wide screens; a
 * collapsible section above the content on phones.
 */
import { BASELINES, MATURITY_LEVELS, RISK_LABEL, RISK_SOURCES } from '@grc/shared/vocabulary';
import { useEffect, useState, type ReactNode } from 'react';

const STATUS = [
  { chip: 'tone-in_place', label: 'Fully in place', text: 'Working across the firm, with evidence.' },
  { chip: 'tone-progress', label: 'Partly in place', text: 'Some systems or steps are covered, not all.' },
  { chip: 'tone-neutral', label: 'Not yet in place', text: 'Not started, or no evidence yet.' },
  { chip: 'tone-neutral', label: 'Awaiting review', text: 'Sent through the assistant; National IT has not checked it yet.' },
  { chip: 'tone-reviewed', label: 'Reviewed by National IT', text: 'National IT checked the evidence and agreed the rating.' },
];

/** Where each control stands on the self-assessment worklist (the same labels as its rows). */
const WORKLIST = [
  { chip: 'tone-attention', label: 'Returned with questions', text: 'National IT asked a question before agreeing the rating. Answer it through the assistant.' },
  { chip: 'tone-neutral', label: 'Not rated', text: 'Nobody has rated it yet, and CDW has not assessed it.' },
  { chip: 'tone-attention', label: 'Not sent to National IT', text: 'Rated by the firm but not yet sent to National IT. Send it through the assistant.' },
  { chip: 'tone-neutral', label: 'Awaiting review', text: 'Sent through the assistant; National IT has not checked it yet.' },
  { chip: 'tone-reviewed', label: 'Reviewed by National IT', text: 'National IT checked the evidence and agreed the rating.' },
  { chip: 'tone-in_place', label: 'CDW: Fully in place', text: "The rating is CDW's 2026 result: fully in place, partly in place or not yet in place. Update it through the assistant if it has changed." },
];

const SOURCES = [
  { label: 'Assessed by CDW, 2026', text: 'The independent risk assessment and penetration test.' },
  { label: 'Firm self-assessment, March 2025', text: "The network survey each firm answered about itself." },
  { label: 'Self-assessed by the firm, 2026', text: "The firm's own current rating, until National IT reviews it." },
];

export function useWide(min = 1100) {
  const query = `(min-width: ${min}px)`;
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const on = () => setWide(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, [query]);
  return wide;
}

/** Page layout with the Explained panel (and anything else) in a side column. */
export function WithPanel({ children, panel, wide = false }: { children: ReactNode; panel: ReactNode; wide?: boolean }) {
  return (
    <div className={wide ? 'with-panel wide-side' : 'with-panel'}>
      <div className="with-panel-main">{children}</div>
      <aside className="with-panel-side">{panel}</aside>
    </div>
  );
}

/** Open by default on wide screens; `collapsed` keeps it folded (on the control page, where the assistant leads). */
export function ExplainedPanel({ show = ['maturity', 'status', 'baselines', 'risk', 'sources'], collapsed = false }: { show?: Array<'maturity' | 'status' | 'baselines' | 'risk' | 'sources' | 'worklist'>; collapsed?: boolean }) {
  const wide = useWide();
  return (
    <details className="explained" open={wide && !collapsed} id="maturity">
      <summary>
        <strong>Explained</strong> <span className="muted small">what each term means</span>
      </summary>
      {show.includes('maturity') && (
        <section aria-label="Maturity">
          <h3>Maturity (0 to 5)</h3>
          <ol className="scale">
            {MATURITY_LEVELS.map((m) => (
              <li key={m.level}>
                <span className="lvl">{m.level}</span>
                <span>
                  <strong>{m.name}.</strong> {m.plain}
                </span>
              </li>
            ))}
          </ol>
        </section>
      )}
      {show.includes('status') && (
        <section aria-label="Status">
          <h3>Status</h3>
          <dl className="terms">
            {STATUS.map((s) => (
              <div key={s.label}>
                <dt>
                  <span className={`chip ${s.chip}`}>{s.label}</span>
                </dt>
                <dd>{s.text}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}
      {show.includes('worklist') && (
        <section aria-label="Self-assessment">
          <h3>Self-assessment</h3>
          <dl className="terms">
            {WORKLIST.map((s) => (
              <div key={s.label}>
                <dt>
                  <span className={`chip ${s.chip}`}>{s.label}</span>
                </dt>
                <dd>{s.text}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}
      {show.includes('baselines') && (
        <section aria-label="Baselines">
          <h3>Baselines</h3>
          <dl className="terms">
            <div>
              <dt>{BASELINES.btc.name}</dt>
              <dd>Share of the 56 CIS controls every firm in the network is expected to have (CIS Implementation Group 1) that are in place, fully or partly.</dd>
            </div>
            <div>
              <dt>{BASELINES.bti.name}</dt>
              <dd>Share of the 39 controls Baker Tilly International requires in its Resilience Policy (the 25 Wave 1 requirements as BTI lists them, and the 14 in Wave 2) that are in place, fully or partly.</dd>
            </div>
            <div>
              <dt>In place</dt>
              <dd>
                A control counts as in place when it is fully or partly in place. Each figure also says how many are only partly in place. Maturity (0 to 5) shows how
                established the practices are, and so how much more work a partly control needs.
              </dd>
            </div>
            <div>
              <dt>Network median</dt>
              <dd>The middle firm's figure, from five or more firms. No firm is named.</dd>
            </div>
          </dl>
        </section>
      )}
      {show.includes('risk') && (
        <section aria-label="Priority">
          <h3>Priority</h3>
          <dl className="terms">
            <div>
              <dt>
                <span className="risk risk-critical">{RISK_LABEL.critical}</span>
              </dt>
              <dd>Stops the most common attacks, including ransomware. Do these first.</dd>
            </div>
            <div>
              <dt>
                <span className="risk risk-high">{RISK_LABEL.high}</span>
              </dt>
              <dd>Blocks many attacks, or must be in place for other controls to work.</dd>
            </div>
            <div>
              <dt>
                <span className="risk risk-medium">{RISK_LABEL.medium}</span>
              </dt>
              <dd>Supporting process or awareness. Still required.</dd>
            </div>
          </dl>
          <p className="muted small">
            Based on{' '}
            {Object.values(RISK_SOURCES).map((s, i, all) => (
              <span key={s.url}>
                <a href={s.url} target="_blank" rel="noreferrer">
                  {s.title}
                </a>
                {i < all.length - 1 ? ', ' : '.'}
              </span>
            ))}
          </p>
        </section>
      )}
      {show.includes('sources') && (
        <section aria-label="Where figures come from">
          <h3>Where figures come from</h3>
          <dl className="terms">
            {SOURCES.map((s) => (
              <div key={s.label}>
                <dt>{s.label}</dt>
                <dd>{s.text}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}
    </details>
  );
}
